/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import * as Yup from 'yup';
import { defineMessages } from 'react-intl';

import { metadataEditorUtils } from 'Common';

const messages = defineMessages({
  requiredField: 'Required',
  fieldContainsInvalidChars: 'Contains invalid characters (permitted: {permitted})',
  fieldContainsInvalidCharsForbidden: 'Contains invalid characters (forbidden: {forbidden})',
  fieldTooShort: 'Too short (min length: {min})',
  fieldTooLong: 'Too long (max length: {max})',
  fieldOneOf: 'Must be one of {enum}',
  containsReservedWord: 'Cannot contain reserved word',
});

/*
 * Common name field used on many IM HTTP resources.
 * See https://github.ibm.com/wiotp-infomgmt/com.ibm.iotcloud.internal.infomgmt.api/blob/5dced9cd921f5faa114de02969994cb127f0faf1/src/main/java/com/ibm/iotcloud/internal/infomgmt/api/model/NamedInfoMgmtResource.java#L23
 */
const WIOTP_IM_NAME_MAX_LENGTH = 128;
const WIOTP_IM_NAME = formatMessage =>
  Yup.string()
    .required(formatMessage(messages.requiredField))
    .matches(/^(?![ ]+$)/, formatMessage(messages.requiredField)) // cannot consist only of spaces
    .max(
      WIOTP_IM_NAME_MAX_LENGTH,
      formatMessage(messages.fieldTooLong, { max: WIOTP_IM_NAME_MAX_LENGTH })
    )
    .matches(
      /^[\w \-_.]+$/,
      formatMessage(messages.fieldContainsInvalidChars, {
        permitted: 'alphanumerics, space, -, _ and .',
      })
    );

/*
 * Common description field used on many IM HTTP resources.
 * See https://github.ibm.com/wiotp-infomgmt/com.ibm.iotcloud.internal.infomgmt.api/blob/5dced9cd921f5faa114de02969994cb127f0faf1/src/main/java/com/ibm/iotcloud/internal/infomgmt/api/model/NamedInfoMgmtResource.java#L23
 */
const WIOTP_IM_DESCRIPTION_MAX_LENGTH = 1024;
const WIOTP_IM_DESCRIPTION = formatMessage =>
  Yup.string().max(
    WIOTP_IM_DESCRIPTION_MAX_LENGTH,
    formatMessage(messages.fieldTooLong, {
      max: WIOTP_IM_DESCRIPTION_MAX_LENGTH,
    })
  );

const WIOTP_IM_LI_ALIAS_MIN_LENGTH = 1;
const WIOTP_IM_LI_ALIAS_MAX_LENGTH = 36;
const WIOTP_IM_LI_ALIAS = formatMessage =>
  Yup.string()
    .min(
      WIOTP_IM_LI_ALIAS_MIN_LENGTH,
      formatMessage(messages.fieldTooShort, {
        min: WIOTP_IM_LI_ALIAS_MIN_LENGTH,
      })
    )
    .max(
      WIOTP_IM_LI_ALIAS_MAX_LENGTH,
      formatMessage(messages.fieldTooLong, {
        max: WIOTP_IM_LI_ALIAS_MAX_LENGTH,
      })
    )
    .matches(
      /^[A-Za-z0-9\-_.]+$/,
      formatMessage(messages.fieldContainsInvalidChars, {
        permitted: 'alphanumerics, -, _ and .',
      })
    );

const WIOTP_IM_LI_RESOURCE = formatMessage =>
  Yup.object({
    name: WIOTP_IM_NAME(formatMessage),
    description: WIOTP_IM_DESCRIPTION(formatMessage),
    alias: WIOTP_IM_LI_ALIAS(formatMessage),
  });

// no inputs directly control values for schema resource,
// so no Yup validation is required
const WIOTP_IM_SCHEMA_RESOURCE = formatMessage__ => Yup.object({});

const VALID_NSTRATS = ['on-every-event', 'on-state-change', 'never'];
export const WIOTP_IM_MAPPING_RESOURCE = formatMessage =>
  Yup.object({
    logicalInterfaceId: Yup.string().required(),
    notificationStrategy: Yup.string().oneOf(
      VALID_NSTRATS,
      formatMessage(messages.fieldOneOf, { enum: VALID_NSTRATS.join(', ') })
    ),
  });

const WIOTP_IM_EVENT_ID = formatMessage =>
  Yup.string()
    .required(formatMessage(messages.requiredField))
    .matches(/^(?![ ]+$)/, formatMessage(messages.requiredField)) // cannot consist only of spaces
    .matches(
      /^[^#/+\\.]*$/,
      formatMessage(messages.fieldContainsInvalidCharsForbidden, {
        forbidden: '+ # \\ / .',
      })
    );

const VALID_DIMENSION_TYPES = Object.values(metadataEditorUtils.DATA_TYPES);

// Awaiting info on what characters / keywords are forbidden in dimensions. This is just a placeholder for now
const DIMENSION_KEY_REGEX = /^[^\\]*$/;
const DIMENSION_VALUE_REGEX = /^[^\\]*$/;

const DATABASE_RESERVED_WORDS = [
  'DEVICETYPE',
  'DEVICEID',
  'LOGICALINTERFACE_ID',
  'EVENTTYPE',
  'FORMAT',
  'RCV_TIMESTAMP_UTC',
  'UPDATED_UTC',
];

const isReservedWord = value => DATABASE_RESERVED_WORDS.some(x => x === value);

const WIOTP_DIMENSIONS = formatMessage =>
  Yup.array(
    Yup.object({
      key: Yup.string()
        .required(formatMessage(messages.requiredField))
        .matches(
          DIMENSION_KEY_REGEX,
          formatMessage(messages.fieldContainsInvalidCharsForbidden, { forbidden: '.' })
        )
        .test(
          'containsReservedWord',
          formatMessage(messages.containsReservedWord),
          value => !isReservedWord(value)
        ),
      type: Yup.string()
        .required(formatMessage(messages.requiredField))
        .oneOf(
          VALID_DIMENSION_TYPES,
          formatMessage(messages.fieldOneOf, { enum: VALID_DIMENSION_TYPES.join(', ') })
        ),
      value: Yup.mixed()
        .when('type', {
          is: metadataEditorUtils.DATA_TYPES.STRING,
          then: Yup.string().matches(
            DIMENSION_VALUE_REGEX,
            formatMessage(messages.fieldContainsInvalidCharsForbidden, { forbidden: '.' })
          ),
        })
        .when('type', {
          is: metadataEditorUtils.DATA_TYPES.NUMBER,
          then: Yup.number().typeError('NOP'),
        }),
    })
  );

export default {
  WIOTP_IM_NAME,
  WIOTP_IM_DESCRIPTION,
  WIOTP_IM_LI_ALIAS,
  WIOTP_IM_LI_RESOURCE,
  WIOTP_IM_SCHEMA_RESOURCE,
  WIOTP_IM_MAPPING_RESOURCE,
  WIOTP_IM_EVENT_ID,
  WIOTP_DIMENSIONS,
};
